import React, { useCallback, useState } from 'react';
import DataTable from 'react-data-table-component';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { faBarsFilter, faClose } from '@fortawesome/pro-solid-svg-icons';
import { Col, Form, InputGroup, Row, Button } from 'react-bootstrap';
import './AppDataTable.scss';
import { faArrowUpArrowDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcons } from '@dcam/dcam_flhealth_component_library';
//import 'react-date-range/dist/styles.css'; // main style file
//import 'react-date-range/dist/theme/default.css'; // theme css file
import { addDays } from 'date-fns';

interface AppDataTableProps {
    data: any[];
    columns: any[];
    loading?: boolean;
    pagination?: boolean;
    conditionalRowStyles?: any[];
    tableHeading?: React.ReactNode | string;
    filter?: boolean;
    search?: boolean;
    filtersData?: any[];
    searchCallBack?: Function;
    filterCallBack?: Function;
    customStyles?: any;
    filterColumnsPerRow?: number;
    expandableRows?: any;
    expandableRowsComponent?: any;
    expandableRowsComponentProps?: any;
    expandableRowExpanded?: any | null;
    onSelectedRowsChange?: (selected: {
        allSelected: boolean;
        selectedCount: number;
        selectedRows: any;
    }) => void;
    selectableRows?: boolean;
    noDataComponent?: React.ReactNode;
}

export const AppDataTable: React.FC<AppDataTableProps> = ({
    data,
    columns,
    pagination,
    conditionalRowStyles,
    loading,
    tableHeading,
    filter,
    search,
    filtersData,
    searchCallBack,
    customStyles,
    filterCallBack,
    filterColumnsPerRow = 4,
    expandableRows,
    expandableRowExpanded,
    expandableRowsComponent,
    expandableRowsComponentProps,
    onSelectedRowsChange,
    selectableRows,
    noDataComponent,
    ...props
}) => {
    const [searchKeyword, setSearchKeyword] = useState('');
    const [filterForm, setFilterForm] = useState<any>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [openRangePicker, setOpenRangePicker] = useState<any>(null);
    const [dateRange, setDateRange] = useState<any>(null)

    const toggleShow = useCallback(() => setIsOpen(!isOpen), [isOpen]);

    const cols = `col col-md-${12 / filterColumnsPerRow} mb-2`;

    const rows: JSX.Element[] = [];
    const filtersDataObj = filtersData ? filtersData : [];
    for (let i = 0; i < filtersDataObj.length; i += filterColumnsPerRow) {
        const rowColumns = filtersDataObj.slice(i, i + filterColumnsPerRow);

        const row = (
            <div key={'row-' + i} className="row mb-2">
                {rowColumns?.map((val: any, index: any) => {
                    if (val.type === 'SELECT') {
                        return (
                            <div key={'select-col-' + index} className={cols}>
                                <Form.Group className="mb-1" controlId={val.key}>
                                    <Form.Select
                                        name={val.key}
                                        value={filterForm?.[val.key] || ''}
                                        onChange={(e: any) => {
                                            handleFilterForm(val, e.target.value);
                                            val?.handleOnChange && val?.handleOnChange(e.target.value);
                                        }}
                                        className="form-select-sm"
                                    >
                                        <option value="">{val.title}</option>
                                        {val.values.map((data: any) => (
                                            <option key={data.val} value={data.val}>
                                                {data.text}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </div>
                        );
                    } else if (val.type === 'DATE') {
                        return (
                            <div key={'date-col-' + index} className={cols}>
                                <Form.Group className="mb-1" controlId={val.key}>
                                    <input
                                        type='date'
                                        name={val.key}
                                        value={filterForm?.[val.key] || ''}
                                        onChange={(e: any) =>
                                            handleFilterForm(val, e.target.value)
                                        }
                                        className="form-select-sm form-control" />
                                </Form.Group>
                            </div>
                        );
                    }
                })}
            </div>
        );

        rows.push(row);
    }

    const handleFilterForm = (data: any, fieldValue: any) => {
        let obj = {
            ...filterForm,
            [data.key]: fieldValue,
        };
        setFilterForm(obj);
    };

    const handleFilterClear = () => {
        setFilterForm(null);
        setDateRange(null);
        setOpenRangePicker(null);
        if (filterCallBack) {
            filterCallBack(null);
        }
    };

    const handleFilterApply = () => {
        if (filterForm) {
            Object.keys(filterForm).forEach((key) => {
                if (filterForm[key] === '' || !filterForm[key]) {
                    delete filterForm[key];
                }
            });
        }
        if (filterCallBack) {
            filterCallBack(filterForm);
        }
        setOpenRangePicker(null);
    };

    const handleKeyDown = useCallback(
        (e: any) => {
            let value = e?.target?.value;
            if (e.key === 'Enter' && searchCallBack) {
                searchCallBack(value);
            }
        },
        [searchKeyword, searchCallBack]
    );

    const handleSearchKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (!value && searchCallBack) {
            searchCallBack(value);
        } 
        setSearchKeyword(value);
    }

    return (
        <div className="AppDatatable row">
            <div className="col col-md-12">
                <Row className="mb-3 heading m-auto">
                    <Col md={5}>
                        {tableHeading ? (
                            <h5>
                                <strong>{tableHeading}</strong>
                            </h5>
                        ) : (
                            ''
                        )}
                    </Col>
                    <Col md={7} className="div-right-center">
                        <Row>
                            {filter ? (
                                <Col md="auto">
                                    <button
                                        className="form-control"
                                        type="button"
                                        onClick={toggleShow}
                                    >
                                        <div className="filter">
                                            {/*<span>*/}
                                            {/*    <FontAwesomeIcons iconName={faBarsFilter} />*/}
                                            {/*</span>*/}
                                            Filter
                                        </div>
                                    </button>
                                </Col>
                            ) : (
                                ''
                            )}

                            {search && (
                                <Col md="auto">
                                    <div>
                                        <InputGroup>
                                            <Form.Control
                                                aria-label="Search by Keywords"
                                                value={searchKeyword}
                                                onChange={handleSearchKeywordChange}
                                                onBlur={(e) => setSearchKeyword(e.target.value)}
                                                onKeyDown={(e: any) => handleKeyDown(e)}
                                                placeholder="Search by Keywords"
                                            />
                                            <InputGroup.Text
                                                className="search-icon"
                                                onClick={() =>
                                                    searchCallBack && searchCallBack(searchKeyword)
                                                }
                                            >
                                                {/* Assuming you have FontAwesomeIcons configured properly */}
                                                <FontAwesomeIcons iconName={faMagnifyingGlass} />
                                            </InputGroup.Text>
                                        </InputGroup>
                                    </div>
                                </Col>
                            )}


                        </Row>
                    </Col>
                </Row>
                {isOpen && (
                    <Row className="mb-3 heading filter-fields m-auto">
                        <Col md={12}>
                            <Row className="mb-0">
                                <Col
                                    md={10}
                                    className="div-right-center"
                                    style={{ borderRight: '1px solid #CED4DA' }}
                                >
                                    <Row className="mb-0">{rows}</Row>
                                </Col>
                                <Col md={2} className="div-right-center">
                                    <Row className="mb-0">
                                        <Col xs={6}>
                                            <Button
                                                variant="outline"
                                                type="button"
                                                onClick={handleFilterClear}
                                                disabled={!filterForm}
                                                className="px-4 btn-clear"
                                            >
                                                <u>Clear</u>
                                            </Button>
                                        </Col>
                                        <Col xs={6} className="text-right">
                                            <Button
                                                variant="primary"
                                                type="button"
                                                disabled={!filterForm}
                                                onClick={handleFilterApply}
                                                className="submit-btn px-4 btn-apply"
                                            >
                                                Apply
                                            </Button>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                )}
                <DataTable
                    columns={columns}
                    data={data}
                    pagination={pagination}
                    progressPending={loading}
                    conditionalRowStyles={conditionalRowStyles}
                    onChangeRowsPerPage={console.log}
                    customStyles={customStyles}
                    striped={true}
                    highlightOnHover={true}
                    responsive={true}
                    expandableRows={expandableRows}
                    expandableRowExpanded={expandableRowExpanded}
                    expandableRowsComponent={expandableRowsComponent}
                    expandableRowsComponentProps={expandableRowsComponentProps}
                    onSelectedRowsChange={onSelectedRowsChange}
                    selectableRows={selectableRows}
                    noDataComponent={noDataComponent || <div className="text-center p-3">There are no records to display</div>}
                    persistTableHead={true} // Ensures the column headers remain visible
                    expandableIcon={{ collapsed: <></>, expanded: <></> }}
                />
            </div>
        </div>
    );
};

export default AppDataTable;
